const numberFormatter = new Intl.NumberFormat('en-US')

function isBlankishDisplay(value) {
  if (value === null || value === undefined || value === '') return true
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase()
    return !s || s === 'nan' || s === 'none' || s === 'null' || s === '<na>'
  }
  return false
}

export function formatNumber(value) {
  if (isBlankishDisplay(value)) return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return '—'
  return numberFormatter.format(n)
}

/** Stored ratios (e.g. 0.0837) → display percent (8.37%). No magnitude heuristics. */
export function formatPercent(value, { signed = false } = {}) {
  if (isBlankishDisplay(value)) return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return '—'
  const pct = n * 100
  const abs = Math.abs(pct)
  let body
  if (abs >= 100) body = pct.toFixed(0)
  else if (abs >= 10) body = pct.toFixed(1)
  else body = pct.toFixed(2)
  if (signed && pct > 0) return `+${body}%`
  return `${body}%`
}

/**
 * Values already stored as percentage points (e.g. Luminate WoW ``% Chg`` = 12.5 for 12.5%).
 * Do not ×100 — AGENTS ratio rule applies only when the stored form is a fraction.
 */
export function formatPercentPoints(value, { signed = false, maxDecimals = null } = {}) {
  if (isBlankishDisplay(value)) return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return '—'
  const abs = Math.abs(n)
  let body
  if (maxDecimals != null) {
    body = String(Number(n.toFixed(maxDecimals)))
  } else if (abs >= 100) body = n.toFixed(0)
  else if (abs >= 10) body = n.toFixed(1)
  else body = n.toFixed(2)
  if (signed && n > 0) return `+${body}%`
  return `${body}%`
}

/** Luminate / summary WoW columns named ``% Chg`` or ``% Chg TW`` etc. */
export function isPctChgColumn(column) {
  const c = String(column || '')
  return c === '% Chg' || c.startsWith('% Chg ')
}

/** Multiweek dated stream columns: ``Streams YYYY-MM-DD``. */
export function isDatedStreamsColumn(column) {
  return /^Streams \d{4}-\d{2}-\d{2}$/.test(String(column || ''))
}

/** Multiweek WoW columns: ``% Chg YYYY-MM-DD → YYYY-MM-DD``. */
export function isMultiweekPctChgColumn(column) {
  return /^% Chg \d{4}-\d{2}-\d{2} → \d{4}-\d{2}-\d{2}$/.test(String(column || ''))
}

/**
 * WoW % Chg column that ends at this stream week (change into that week from prior).
 * ``Streams 2026-07-10`` → ``% Chg 2026-07-03 → 2026-07-10``.
 */
export function pctChgColumnForStreamsWeek(streamsColumn) {
  const m = String(streamsColumn || '').match(/^Streams (\d{4}-\d{2}-\d{2})$/)
  if (!m) return null
  const weekTo = m[1]
  const d = new Date(`${weekTo}T12:00:00Z`)
  if (Number.isNaN(d.getTime())) return null
  d.setUTCDate(d.getUTCDate() - 7)
  const weekFrom = d.toISOString().slice(0, 10)
  return `% Chg ${weekFrom} → ${weekTo}`
}

/** Short header for dated Streams columns (e.g. Jul 10). */
export function shortDatedStreamsHeader(column) {
  const m = String(column || '').match(/^Streams (\d{4}-\d{2}-\d{2})$/)
  if (!m) return null
  const d = new Date(`${m[1]}T12:00:00`)
  if (Number.isNaN(d.getTime())) return m[1]
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/** True when the column stores percentage *points* (not 0–1 ratios). */
export function isPercentPointsColumn(column) {
  const c = String(column || '')
  return (
    isPctChgColumn(c) ||
    c === 'Over-index %' ||
    c === 'Max Over-index %'
  )
}

/** Level shares (e.g. Audio % TW) — display as % without signed + / green|red tone. */
export function isLevelPercentColumn(column) {
  const c = String(column || '')
  return c === 'Audio % TW' || c.startsWith('Audio %')
}

export function isPercentColumn(column, percentColumns = []) {
  const lower = String(column || '').toLowerCase()
  return (
    percentColumns.includes(column) ||
    column.endsWith(' Growth %') ||
    column.includes('%') ||
    lower.includes('engagement rate') ||
    lower.includes('saves rate')
  )
}

export function isGrowthColumn(column) {
  const lower = column.toLowerCase()
  return lower.includes('growth') || lower.includes('% change') || lower.endsWith(' %')
}

/**
 * Opaque platform / mapping identifiers — never thousand-separator or re-parse as quantities.
 * Includes columns that omit "ID" in the artist-mapping sheet (Apple Music, Spotify, YouTube),
 * and the catalog codes on the Operations tables (a UPC is digits, not a quantity).
 */
const PLATFORM_ID_COLUMNS = new Set([
  'Apple Music',
  'Apple',
  'Spotify',
  'YouTube',
  'SoundCloud',
  'Genius',
  'Twitter',
  'Shazam',
  'Chartmetric ID',
  'UPC',
  'ISRC',
])

export function isIdColumn(column) {
  const c = String(column || '')
  if (PLATFORM_ID_COLUMNS.has(c)) return true
  const lower = c.toLowerCase()
  return lower.includes('uid') || lower.includes('id')
}

/** Display IDs as plain digit/string identifiers (no commas; strip float `.0`). */
export function formatId(value) {
  if (value === null || value === undefined || value === '') return '—'
  let s = String(value).trim()
  if (!s || s.toLowerCase() === 'nan' || s.toLowerCase() === 'none') return '—'
  if (/^\d+\.0+$/.test(s)) s = s.replace(/\.0+$/, '')
  return s
}

// ---------------------------------------------------------------------------
// PGP money columns (pub_mappings deal fields)
// ---------------------------------------------------------------------------

/** Whole-USD money columns on the PGP Agenda / Mapping tables. */
export const PGP_MONEY_COLUMNS = new Set(['Committed $', 'Uncommitted'])

/** Free-text column that shows compact USD when it holds a lone number. */
export const PGP_OPTION_COLUMN = 'Option'

export function isPgpMoneyColumn(column) {
  return PGP_MONEY_COLUMNS.has(String(column || ''))
}

/**
 * Parse a dollar amount to a whole-USD integer.
 * Accepts `1250`, `$1,250`, `$1,250.40` (rounded). Blank → null.
 */
export function parseCommittedMoney(value) {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.round(value) : null
  }
  const text = String(value).trim()
  if (!text || ['nan', 'none', 'null', '—'].includes(text.toLowerCase())) return null
  const cleaned = text.replace(/[$,\s]/g, '')
  if (!cleaned) return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? Math.round(n) : null
}

/** Drop trailing zeros so 78.75 → "78.75", 67.50 → "67.5", 150.00 → "150". */
function trimCompactSuffix(n) {
  const s = n.toFixed(2)
  if (s.endsWith('.00')) return s.slice(0, -3)
  if (s.endsWith('0')) return s.slice(0, -1)
  return s
}

/** Compact USD: `$78.75k`, `$67.5k`, `$150k`, `$1.55M`. Under $1k stays `$999`. */
export function formatCompactMoney(value, { empty = '—' } = {}) {
  const parsed = parseCommittedMoney(value)
  if (parsed == null) return empty
  const sign = parsed < 0 ? '-' : ''
  const abs = Math.abs(parsed)
  if (abs >= 1_000_000) return `${sign}$${trimCompactSuffix(abs / 1_000_000)}M`
  if (abs >= 1_000) return `${sign}$${trimCompactSuffix(abs / 1_000)}k`
  return `${sign}$${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

/** Full USD: `$1,234`. Used for the hover title behind the compact display. */
export function formatFullMoney(value, { empty = '—' } = {}) {
  const parsed = parseCommittedMoney(value)
  if (parsed == null) return empty
  return `$${parsed.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

/** Option cell: compact USD when the value is a lone number, else raw text. */
export function formatPgpOption(value, { empty = '—' } = {}) {
  if (value === null || value === undefined || value === '') return empty
  const text = String(value).trim()
  if (!text || ['nan', 'none', 'null'].includes(text.toLowerCase())) return empty
  // Only a bare amount formats; "2 x 100k" and similar stay as written.
  if (/^\$?\s*-?[\d,]+(\.\d+)?$/.test(text)) {
    return formatCompactMoney(text, { empty })
  }
  return text
}

/** Luminate track ``Residual`` and agg ``Max Residual`` (TW/RTD over-index units). */
export function isResidualColumn(column) {
  return column === 'Residual' || column === 'Max Residual'
}

export function formatResidual(value, { signed = true } = {}) {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return '—'
  const body = n.toFixed(3)
  if (signed && n > 0) return `+${body}`
  return body
}

function mmSs(totalSeconds) {
  const total = Math.max(0, Math.floor(totalSeconds))
  const mm = String(Math.floor(total / 60)).padStart(2, '0')
  const ss = String(total % 60).padStart(2, '0')
  return `${mm}:${ss}`
}

/**
 * Catalog track length as ``MM:SS``. Accepts seconds (number or digit string),
 * ``M:SS``, ``H:MM:SS`` and the ``MM:SS:00`` dump shape.
 * Returns '' when the value is blank or cannot be read as a duration.
 */
export function formatDurationMmSs(value) {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'boolean') return ''
  if (typeof value === 'number') return Number.isFinite(value) ? mmSs(value) : ''
  const s = String(value).trim()

  const mmss = s.match(/^(\d{1,4}):([0-5]?\d)$/)
  if (mmss) return mmSs(Number(mmss[1]) * 60 + Number(mmss[2]))

  const hms = s.match(/^(\d{1,4}):([0-5]?\d):([0-5]?\d)$/)
  if (hms) {
    const [a, b, c] = hms.slice(1).map(Number)
    if (a !== 0 && c === 0) return mmSs(a * 60 + b) // MM:SS:00 dump shape
    return mmSs(a * 3600 + b * 60 + c)
  }

  if (/^\d{1,6}$/.test(s)) return mmSs(Number(s))
  return ''
}

export function formatCell(column, value, percentColumns = []) {
  if (column === 'Duration') {
    if (value === null || value === undefined || value === '') return '—'
    return formatDurationMmSs(value) || String(value)
  }
  if (isTimestampColumn(column)) {
    return formatTimestamp(value)
  }
  if (isResidualColumn(column)) {
    return formatResidual(value)
  }
  if (isPctChgColumn(column)) {
    return formatPercentPoints(value, { signed: true, maxDecimals: 1 })
  }
  if (isPercentPointsColumn(column)) {
    return formatPercentPoints(value, { signed: true })
  }
  if (isPercentColumn(column, percentColumns)) {
    return formatPercent(value, { signed: !isLevelPercentColumn(column) })
  }
  if (isPgpMoneyColumn(column)) {
    return formatCompactMoney(value)
  }
  if (column === PGP_OPTION_COLUMN) {
    return formatPgpOption(value)
  }
  if (isIdColumn(column)) {
    return formatId(value)
  }
  if (typeof value === 'number' || (typeof value === 'string' && value !== '' && !Number.isNaN(Number(value)))) {
    const asNum = Number(value)
    if (!Number.isNaN(asNum)) {
      return formatNumber(asNum)
    }
  }
  if (value === null || value === undefined || value === '') return '—'
  return String(value)
}

export function formatSnapshotDate(value) {
  if (!value) return 'No snapshot'
  const d = new Date(`${value}T00:00:00`)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** ISO / DB timestamps → short local datetime (e.g. mapping Updated At). */
export function formatTimestamp(value) {
  if (isBlankishDisplay(value)) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function isTimestampColumn(column) {
  return column === 'Updated At' || column === 'Created At'
}

export function percentTone(value) {
  const n = Number(value)
  if (Number.isNaN(n) || n === 0) return 'neutral'
  return n > 0 ? 'positive' : 'negative'
}
